import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { engineRulesAPI } from '../../services/api'
import type { EngineVersionRecord } from '../../types'
import { parseApiDate } from '../../utils/signalExpiry'
import TerminalPanel from '../dashboard/TerminalPanel'
import { formatR, pnlClass } from './recordUI'

const day = (iso: string | null) => (iso ? format(parseApiDate(iso), 'MMM d, yyyy') : '—')

/** A setting's value as a reader can scan it. */
export const settingValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'not set'
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'none'
  if (typeof value === 'boolean') return value ? 'on' : 'off'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const NAMES: Record<string, string> = {
  decider: 'Phase 2 decided by',
  logic_revision: 'Decision code revision',
}

/** "50.0% (9–91%)": the gross win rate and its 95% interval; "—" with nothing closed. */
export const winRateText = (v: EngineVersionRecord): string => {
  if (v.win_rate === null) return '—'
  const { low, high, measured } = v.win_rate_interval ?? {}
  const range = measured && low != null && high != null ? ` (${low.toFixed(0)}–${high.toFixed(0)}%)` : ''
  return `${v.win_rate.toFixed(1)}%${range}`
}

/**
 * The engine's record split by the engine version that published it (GET
 * /api/public/engine-versions). A change to how the engine decides (a
 * setting, the decision code, or who decides at Phase 2) makes a new version,
 * so a record from before a change is never read as the record of the engine
 * running now. Gross, like every figure here. Nothing renders until at least
 * one version has been recorded, or if the split can't load.
 */
const EngineVersionsPanel = () => {
  const [versions, setVersions] = useState<EngineVersionRecord[]>([])

  useEffect(() => {
    let live = true
    Promise.resolve()
      .then(() => engineRulesAPI.versions())
      .then((rows) => live && setVersions(rows ?? []))
      .catch(() => {})
    return () => {
      live = false
    }
  }, [])

  if (!versions.some((v) => v.version !== null)) return null
  const changed = versions.filter((v) => v.changes && v.changes.length)
  return (
    <TerminalPanel
      title="Record by engine version"
      tone="slate"
      subtitle={
        <p className="text-xs text-gray-400">
          Each change to how the engine decides starts a new version, and each version&rsquo;s record is shown on its
          own, before fees. The range beside a win rate is its 95% interval: a few closes say little.
        </p>
      }
    >
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="engine-versions">
          <thead>
            <tr className="border-b border-terminal-border text-left text-[11px] uppercase tracking-wider text-gray-500">
              <th className="px-3 py-2">Version</th>
              <th className="px-3 py-2">Published</th>
              <th className="px-3 py-2 text-right">Signals</th>
              <th className="px-3 py-2 text-right">Closed</th>
              <th className="px-3 py-2 text-right">Wins / losses</th>
              <th className="px-3 py-2 text-right">Win rate</th>
              <th className="px-3 py-2 text-right">Avg R before fees</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.version ?? 'before'} className="border-b border-terminal-border/60 text-gray-300">
                <td className="px-3 py-2 whitespace-nowrap">
                  {v.label}
                  {v.version && <span className="ml-2 font-mono text-[11px] text-gray-500">{v.version}</span>}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {day(v.first_published_at)} – {day(v.last_published_at)}
                </td>
                <td className="px-3 py-2 text-right">{v.published}</td>
                <td className="px-3 py-2 text-right">{v.closed}</td>
                <td className="px-3 py-2 text-right">
                  {v.wins} / {v.losses}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">{winRateText(v)}</td>
                <td className={`px-3 py-2 text-right ${v.avg_r_gross === null ? 'text-gray-500' : pnlClass(v.avg_r_gross)}`}>
                  {v.avg_r_gross === null ? '—' : formatR(v.avg_r_gross)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {changed.length > 0 && (
        <ul className="mt-3 space-y-1 text-xs text-gray-400" data-testid="engine-version-changes">
          {changed.map((v) => (
            <li key={v.version ?? 'before'}>
              <span className="text-gray-300">{v.label}</span> changed:{' '}
              {(v.changes ?? [])
                .map((c) => `${NAMES[c.name] ?? c.name} ${settingValue(c.from)} → ${settingValue(c.to)}`)
                .join('; ')}
            </li>
          ))}
        </ul>
      )}
    </TerminalPanel>
  )
}

export default EngineVersionsPanel
